import { differentiateSiblingLabels } from './shortenChartLegendNames.js';
import {
  extractShortLabelFromQuestion,
  localizeGroupItemTitle,
  resolvePolymarketDisplayLabel,
} from './resolvePolymarketDisplayLabel.js';

/**
 * PM 多 outcome 行标签（A + B）：
 * A. 有 Gamma/平台 `groupItemTitle` → 官方短标（zh 下英文月日本地化）
 * B. 缺短标或短标撞名 → 优先「单句结构抽取」若兄弟间互异；
 *    否则对完整 `question` 做兄弟差分；再不行用完整问句（禁止撞名）。
 *
 * 图表主标题仍应使用集合 title / 完整 question，勿把本函数结果当大标题。
 *
 * @param {Array<object|null|undefined>} markets
 * @param {string} [locale]
 * @returns {string[]}
 */
export function resolvePolymarketDisplayLabels(markets, locale = 'en') {
  if (!Array.isArray(markets) || markets.length === 0) return [];

  const loc = String(locale || 'en');
  const questions = markets.map((m) => String(m?.question || '').trim());
  const official = markets.map((m) => {
    const short = String(m?.groupItemTitle || '').trim();
    return short ? localizeGroupItemTitle(short, loc) : '';
  });

  if (official.every(Boolean) && isUniqueNonEmpty(official)) {
    return official;
  }

  const extracted = questions.map((q) => {
    if (!q) return '';
    const got = extractShortLabelFromQuestion(q, loc);
    return got && got !== q ? got : '';
  });
  const extractedFilled = questions.map((q, i) => extracted[i] || q);
  const extractedOk =
    markets.length >= 2 && extracted.some(Boolean) && isUniqueNonEmpty(extractedFilled);

  const siblingRaw =
    markets.length >= 2 ? differentiateSiblingLabels(questions) : questions.slice();
  const siblingOk =
    markets.length >= 2 &&
    isUniqueNonEmpty(siblingRaw) &&
    siblingRaw.some((label, i) => label.length > 0 && label.length < questions[i].length);

  const out = markets.map((m, i) => {
    if (official[i]) return official[i];
    if (extractedOk && extractedFilled[i]) return extractedFilled[i];
    if (siblingOk && siblingRaw[i]) return siblingRaw[i];
    return resolvePolymarketDisplayLabel(m, loc);
  });

  return repairCollisions(out, questions);
}

/**
 * @param {string[]} labels
 * @param {string[]} questions
 */
function repairCollisions(labels, questions) {
  const counts = new Map();
  for (const label of labels) {
    const key = String(label || '')
      .trim()
      .toLowerCase();
    if (!key || key === '—') continue;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return labels.map((label, i) => {
    const key = String(label || '')
      .trim()
      .toLowerCase();
    if (key && key !== '—' && (counts.get(key) || 0) > 1) {
      return questions[i] || label || '—';
    }
    return label || questions[i] || '—';
  });
}

/** @param {string[]} labels */
function isUniqueNonEmpty(labels) {
  const cleaned = labels.map((l) => String(l || '').trim()).filter((l) => l && l !== '—');
  if (cleaned.length !== labels.length) return false;
  if (cleaned.length === 0) return false;
  if (cleaned.length === 1) return true;
  return new Set(cleaned.map((l) => l.toLowerCase())).size === cleaned.length;
}
